import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  fetchActiveSessions,
  revokeSession,
  fetchUsage,
  fetchRedactions,
  fetchEntityRedactions,
  sendEntityRedactionReport,
  fetchEntityUsage,
} from '../api/client';
import UsageChart from './UsageChart';
import RedactionChart from './RedactionChart';
import ExportConversationsModal from './ExportConversationsModal';
import './SettingsPanel.css';

const TABS = [
  { id: 'privacy', label: 'Privacy' },
  { id: 'sessions', label: 'Active Sessions' },
  { id: 'usage', label: 'Usage' },
  { id: 'memory', label: 'Memory' },
  { id: 'api', label: 'API Integration' },
];

function ReportButton({ onSend }) {
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  async function handleClick() {
    setStatus('sending');
    setMessage('');
    try {
      const data = await onSend();
      setStatus('sent');
      setMessage(`Report sent to ${data.sentTo}.`);
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Failed to send report');
    }
  }

  return (
    <div className="settings-report-action">
      <button className="settings-report-btn" onClick={handleClick} disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending…' : 'Email report'}
      </button>
      {message && (
        <p className={`settings-report-message ${status === 'error' ? 'settings-report-message--error' : ''}`}>
          {message}
        </p>
      )}
    </div>
  );
}

function PrivacyTab() {
  const { user } = useAuth();
  const isOrgAdmin = user.userType === 2 && user.role === 1;

  const [redactions, setRedactions] = useState(null);
  const [error, setError] = useState('');
  const [entityRedactions, setEntityRedactions] = useState(null);
  const [entityError, setEntityError] = useState('');

  useEffect(() => {
    fetchRedactions()
      .then((data) => setRedactions(data.monthly))
      .catch((err) => setError(err.message || 'Failed to load redaction history'));
  }, []);

  useEffect(() => {
    if (!isOrgAdmin) return;
    fetchEntityRedactions()
      .then((data) => setEntityRedactions(data.monthly))
      .catch((err) => setEntityError(err.message || 'Failed to load organization redaction history'));
  }, [isOrgAdmin]);

  return (
    <div className="settings-tab-content">
      <a className="settings-hyperlink" href="/privacy" target="_blank" rel="noopener noreferrer">
        Read our full Privacy Policy
      </a>

      <div className="settings-section">
        <h3>How we protect your data</h3>
        <p>
          Every conversation is screened before it ever reaches the AI model. We strip out names, contact
          details, and any other information that could identify a specific person or organization, so the
          model only ever works with de-identified content. Your data stays private throughout — the
          underlying AI never sees who you are.
        </p>
      </div>

      <div className={isOrgAdmin ? 'settings-charts-row' : undefined}>
        <div className={`settings-section ${isOrgAdmin ? 'settings-chart-panel settings-chart-panel--personal' : ''}`}>
          <h3>{isOrgAdmin ? 'Your filtered words per month' : 'Filtered words per month'}</h3>
          {error && <p className="settings-placeholder">{error}</p>}
          {!error && !redactions && <p className="settings-placeholder">Loading redaction history…</p>}
          {!error && redactions && <RedactionChart data={redactions} />}
        </div>

        {isOrgAdmin && (
          <div className="settings-section settings-chart-panel settings-chart-panel--org">
            <div className="settings-chart-panel-header">
              <h3>Organization-wide filtered words per month</h3>
              {entityRedactions && <ReportButton onSend={sendEntityRedactionReport} />}
            </div>
            {entityError && <p className="settings-placeholder">{entityError}</p>}
            {!entityError && !entityRedactions && (
              <p className="settings-placeholder">Loading organization redaction history…</p>
            )}
            {!entityError && entityRedactions && <RedactionChart data={entityRedactions} />}
          </div>
        )}
      </div>
    </div>
  );
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function SessionsTab() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [error, setError] = useState('');
  const [revokingId, setRevokingId] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const data = await fetchActiveSessions();
      setSessions(data.sessions);
      setCurrentSessionId(data.currentSessionId);
    } catch (err) {
      setError(err.message || 'Failed to load active sessions');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRevoke(id) {
    setRevokingId(id);
    try {
      const { revokedCurrent } = await revokeSession(id);
      if (revokedCurrent) {
        logout();
        navigate('/login');
        return;
      }
      setSessions((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      setError(err.message || 'Failed to log out that session');
    } finally {
      setRevokingId(null);
    }
  }

  if (sessions === null && !error) {
    return (
      <div className="settings-tab-content">
        <p className="settings-placeholder">Loading active sessions…</p>
      </div>
    );
  }

  return (
    <div className="settings-tab-content">
      {error && <p className="settings-placeholder">{error}</p>}
      {sessions && sessions.length === 0 && !error && (
        <p className="settings-placeholder">No active sessions found.</p>
      )}
      {sessions && sessions.length > 0 && (
        <div className="sessions-list">
          {sessions.map((s) => (
            <div key={s.id} className="sessions-row">
              <div>
                <div className="sessions-device">
                  {s.device}
                  {s.id === currentSessionId && <span className="sessions-current-badge">This device</span>}
                </div>
                <div className="sessions-meta">
                  Signed in {formatDateTime(s.createdAt)} · Last active {formatDateTime(s.lastSeenAt)}
                  {s.ipAddress ? ` · ${s.ipAddress}` : ''}
                </div>
              </div>
              <button
                className="sessions-revoke-btn"
                onClick={() => handleRevoke(s.id)}
                disabled={revokingId === s.id}
              >
                {revokingId === s.id ? 'Logging out…' : 'Log out'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UsageTab() {
  const { user } = useAuth();
  const isOrgAdmin = user.userType === 2 && user.role === 1;

  const [usage, setUsage] = useState(null);
  const [error, setError] = useState('');
  const [entityUsage, setEntityUsage] = useState(null);
  const [entityError, setEntityError] = useState('');

  useEffect(() => {
    fetchUsage()
      .then(setUsage)
      .catch((err) => setError(err.message || 'Failed to load usage'));
  }, []);

  useEffect(() => {
    if (!isOrgAdmin) return;
    fetchEntityUsage()
      .then(setEntityUsage)
      .catch((err) => setEntityError(err.message || 'Failed to load organization usage'));
  }, [isOrgAdmin]);

  if (error) {
    return (
      <div className="settings-tab-content">
        <p className="settings-placeholder">{error}</p>
      </div>
    );
  }

  if (!usage) {
    return (
      <div className="settings-tab-content">
        <p className="settings-placeholder">Loading usage…</p>
      </div>
    );
  }

  return (
    <div className="settings-tab-content">
      <div className="usage-stat-grid">
        <div className="usage-stat">
          <span className="usage-stat-label">{isOrgAdmin ? 'Your tokens used' : 'Tokens used'}</span>
          <span className="usage-stat-value">{usage.tokensUsed.toLocaleString()}</span>
          <span className="usage-stat-sub">
            {usage.updatedAt ? `Last updated ${formatDateTime(usage.updatedAt)}` : 'No chat activity yet'}
          </span>
        </div>
      </div>

      <div className={isOrgAdmin ? 'settings-charts-row' : undefined}>
        <div className={`settings-section ${isOrgAdmin ? 'settings-chart-panel settings-chart-panel--personal' : ''}`}>
          <h3>{isOrgAdmin ? 'Your monthly usage' : 'Monthly usage'}</h3>
          <UsageChart data={usage.monthly} />
        </div>

        {isOrgAdmin && (
          <div className="settings-section settings-chart-panel settings-chart-panel--org">
            <h3>Organization-wide monthly usage</h3>
            {entityError && <p className="settings-placeholder">{entityError}</p>}
            {!entityError && !entityUsage && <p className="settings-placeholder">Loading organization usage…</p>}
            {!entityError && entityUsage && <UsageChart data={entityUsage.monthly} />}
          </div>
        )}
      </div>
    </div>
  );
}

function MemoryTab() {
  const { user } = useAuth();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="settings-tab-content">
      <div className="settings-section">
        <h3>Export your conversations</h3>
        <p>Download a copy of your chat history, sent to your account email as a PDF, JSON, or both.</p>
      </div>

      <div className="export-date-filters">
        <label className="export-date-field">
          <span>From</span>
          <input type="date" value={startDate} max={endDate || undefined} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <label className="export-date-field">
          <span>To</span>
          <input type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} />
        </label>
      </div>

      <button className="export-trigger-btn" onClick={() => setModalOpen(true)}>
        Export conversations
      </button>

      {modalOpen && (
        <ExportConversationsModal
          email={user.email}
          startDate={startDate || null}
          endDate={endDate || null}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}

function ComingSoonTab({ label }) {
  return (
    <div className="settings-tab-content">
      <p className="settings-placeholder">{label} settings are coming soon.</p>
    </div>
  );
}

export default function SettingsPanel() {
  const [activeTab, setActiveTab] = useState('privacy');

  return (
    <div className="settings-panel">
      <div className="settings-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`settings-tab ${activeTab === tab.id ? 'settings-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'privacy' ? (
        <PrivacyTab />
      ) : activeTab === 'sessions' ? (
        <SessionsTab />
      ) : activeTab === 'usage' ? (
        <UsageTab />
      ) : activeTab === 'memory' ? (
        <MemoryTab />
      ) : (
        <ComingSoonTab label={TABS.find((t) => t.id === activeTab)?.label} />
      )}
    </div>
  );
}
